import type { StarsButtonProps } from '@ps/web-shared-components';
import { StarsButton } from '@ps/web-shared-components';
import type { LottieOptions, LottieRefCurrentProps } from 'lottie-react';
import React, { useCallback, useEffect, useRef } from 'react';

import useLottieLoader from '../../hooks/useLottieLoader';

export interface AnimatedButtonProps {
  animation: LottieOptions['animationData'];
}

const AnimatedButton: React.FC<StarsButtonProps & AnimatedButtonProps> = ({
  animation,
  onClick,
  ...rest
}) => {
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const { LottieComponent } = useLottieLoader();

  const setLottieDisplay = useCallback((display: 'none' | 'block') => {
    if (lottieRef.current?.animationContainerRef.current) {
      lottieRef.current.animationContainerRef.current.style.display = display;
    }
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    setLottieDisplay('block');
    lottieRef.current?.play();
  };

  const handleComplete = () => {
    if (lottieRef.current) {
      lottieRef.current.stop();
      setLottieDisplay('none');
    }
  };

  useEffect(() => {
    const currentLottieRef = lottieRef.current;
    return () => {
      if (currentLottieRef) currentLottieRef.destroy();
    };
  }, []);

  return (
    <>
      {LottieComponent && (
        <LottieComponent
          loop={false}
          autoplay={false}
          lottieRef={lottieRef}
          animationData={animation}
          onComplete={handleComplete}
          style={{ display: 'none' }}
        />
      )}
      <StarsButton {...rest} onClick={handleClick} variant="text" />
    </>
  );
};

export default AnimatedButton;
